import { useEffect, useRef, useState } from 'react'
import { AnchoredOverlay, Button, Checkbox, FieldBlock, Input } from '@orbind/ui'
import {
  ENQUETE_MAX_OPCOES,
  ENQUETE_MIN_OPCOES,
  MENU_DE_ANEXOS,
  type MembroDoCanal,
  type MensagemDoCanal,
  type TipoDeAnexo,
} from '@orbind/core'
import { usePresence } from '../presence/PresenceProvider.tsx'
import { EmojiPicker } from './EmojiPicker.tsx'
import { avatarDoAutor } from './useChat.ts'

/** `@termo` colado no cursor, no começo do texto ou depois de um espaço. */
const MENCAO_NO_CURSOR = /(^|\s)@([\p{L}\p{N}._-]{0,30})$/u

/** Tipo do anexo pelo MIME: é o que decide como o balão desenha. */
function tipoDoArquivo(arquivo: File): TipoDeAnexo {
  if (arquivo.type.startsWith('image/')) return 'imagem'
  if (arquivo.type.startsWith('video/')) return 'video'
  if (arquivo.type.startsWith('audio/')) return 'audio'
  return 'documento'
}

/** Print colado chega como "image.png": ganha data e hora para achar depois. */
function comNomeLegivel(arquivo: File): File {
  if (!/^image\.\w+$/i.test(arquivo.name)) return arquivo
  const agora = new Date()
  const d = (n: number) => String(n).padStart(2, '0')
  const extensao = arquivo.name.split('.').pop() ?? 'png'
  const nome = `imagem-colada-${agora.getFullYear()}${d(agora.getMonth() + 1)}${d(agora.getDate())}-${d(agora.getHours())}${d(agora.getMinutes())}${d(agora.getSeconds())}.${extensao}`
  return new File([arquivo], nome, { type: arquivo.type })
}

function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}

/** Compara ignorando caixa e acento: "@joao" acha "João". */
const normalizar = (t: string) => t.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

/**
 * Compositor: texto, anexos e a citação de quem está sendo respondido.
 *
 * O `+` abre o mesmo cardápio de um mensageiro conhecido. Os quatro primeiros
 * itens abrem seletor de arquivo; os três últimos são formulários curtos, e por
 * isso vivem aqui e não num modal — trocar de contexto para montar uma enquete
 * de duas linhas seria pior que o problema.
 */

export type Estruturado = { kind: 'contato' | 'enquete' | 'evento' | 'gif'; payload: Record<string, unknown> }

export function Composer({
  respondendo,
  onCancelarResposta,
  onEnviar,
  onEnviarArquivo,
  onEnviarEstruturado,
  membros,
  desabilitado,
  avisoDesabilitado,
  onDigitar,
}: {
  respondendo: MensagemDoCanal | null
  onCancelarResposta: () => void
  /** `mencoes` = ids marcados pelo autocompletar cujo @Nome ficou no texto. */
  onEnviar: (texto: string, mencoes: string[]) => void
  onEnviarArquivo: (arquivo: File, tipo: TipoDeAnexo) => void
  onEnviarEstruturado: (dados: Estruturado) => void
  membros: readonly MembroDoCanal[]
  desabilitado: boolean
  avisoDesabilitado: string
  /** A cada tecla; quem recebe decide de quanto em quanto avisa os outros. */
  onDigitar?: () => void
}) {
  const [texto, setTexto] = useState('')
  const [menuAnexo, setMenuAnexo] = useState<HTMLElement | null>(null)
  const [seletorEmoji, setSeletorEmoji] = useState<HTMLElement | null>(null)
  const campo = useRef<HTMLTextAreaElement>(null)
  const { eu } = usePresence()

  // O campo cresce com o texto até o teto do CSS (5 linhas) e depois rola —
  // como no mensageiro de referência (SPEC-20261104-1538). Zerar antes de
  // medir deixa o campo ENCOLHER quando se apagam linhas.
  useEffect(() => {
    const el = campo.current
    if (!el) return
    el.style.height = 'auto'
    // scrollHeight nao inclui a borda; com box-sizing border-box ela entra na altura
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`
  }, [texto])
  /** Quem foi escolhido no @ — id por nome, conferido de novo no envio. */
  const marcados = useRef(new Map<string, string>())
  const [mencao, setMencao] = useState<{ termo: string; inicio: number } | null>(null)
  const [destaque, setDestaque] = useState(0)

  const sugestoes = mencao
    ? membros
        .filter((m) => m.memberId !== eu && normalizar(m.name).includes(normalizar(mencao.termo)))
        .slice(0, 6)
    : []

  const lerMencao = (valor: string, cursor: number) => {
    const achado = MENCAO_NO_CURSOR.exec(valor.slice(0, cursor))
    setMencao(achado ? { termo: achado[2] ?? '', inicio: cursor - (achado[2]?.length ?? 0) - 1 } : null)
    setDestaque(0)
  }

  const escolher = (pessoa: MembroDoCanal) => {
    if (!mencao) return
    const el = campo.current
    const cursor = el?.selectionStart ?? texto.length
    const inserido = `@${pessoa.name} `
    const novo = texto.slice(0, mencao.inicio) + inserido + texto.slice(cursor)
    marcados.current.set(pessoa.memberId, pessoa.name)
    setTexto(novo)
    setMencao(null)
    const pos = mencao.inicio + inserido.length
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(pos, pos)
    })
  }
  const [formulario, setFormulario] = useState<'contato' | 'enquete' | 'evento' | null>(null)
  const inputArquivo = useRef<HTMLInputElement>(null)
  const tipoEscolhido = useRef<TipoDeAnexo>('documento')

  /* ------------------------------------------------ Ctrl+V de arquivo --- */

  /**
   * Arquivos colados (Ctrl+V) esperando o envio — pedido 2026-09-14: copiar uma
   * imagem no Google e colar não fazia nada. Como no mensageiro de referência,
   * o que foi colado aparece em cima do campo com prévia; o texto digitado vai
   * junto, logo depois, e Enter manda tudo.
   */
  const [colados, setColados] = useState<{ id: string; arquivo: File; previa: string | null }[]>([])
  const coladosRef = useRef(colados)
  coladosRef.current = colados

  const anexarColados = (arquivos: File[]) => {
    if (arquivos.length === 0) return
    setColados((atual) => [
      ...atual,
      ...arquivos.map((original) => {
        const arquivo = comNomeLegivel(original)
        return {
          id: `${Date.now()}-${Math.random()}`,
          arquivo,
          previa: arquivo.type.startsWith('image/') ? URL.createObjectURL(arquivo) : null,
        }
      }),
    ])
    requestAnimationFrame(() => campo.current?.focus())
  }

  const removerColado = (id: string) =>
    setColados((atual) => {
      const saindo = atual.find((c) => c.id === id)
      if (saindo?.previa) URL.revokeObjectURL(saindo.previa)
      return atual.filter((c) => c.id !== id)
    })

  // Libera as prévias ao sair da conversa.
  useEffect(
    () => () => {
      for (const c of coladosRef.current) if (c.previa) URL.revokeObjectURL(c.previa)
    },
    [],
  )

  // Colar vale com a conversa aberta mesmo sem o cursor no campo — só não
  // rouba o Ctrl+V de OUTRO campo (busca, formulário, painel do grupo).
  useEffect(() => {
    if (desabilitado) return
    const aoColar = (e: ClipboardEvent) => {
      const alvo = e.target as HTMLElement | null
      const outroCampo =
        alvo && alvo !== campo.current && (alvo.closest('input, textarea, [contenteditable="true"]') !== null)
      if (outroCampo) return
      const dados = e.clipboardData
      if (!dados) return

      const arquivos = [...dados.items]
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null)
      if (arquivos.length > 0) {
        e.preventDefault()
        anexarColados(arquivos)
        return
      }

      // Alguns sites copiam a imagem só como HTML (<img src>): tenta baixar.
      // Se o site não deixar (CORS), segue a colagem normal de texto.
      const html = dados.getData('text/html')
      const src = /<img[^>]+src="(https?:[^"]+)"/i.exec(html)?.[1]
      if (src && dados.getData('text/plain').trim() === '') {
        e.preventDefault()
        void fetch(src)
          .then((r) => (r.ok ? r.blob() : Promise.reject()))
          .then((blob) => {
            if (!blob.type.startsWith('image/')) return
            anexarColados([new File([blob], `imagem.${blob.type.split('/')[1] ?? 'png'}`, { type: blob.type })])
          })
          .catch(() => undefined)
      }
    }
    window.addEventListener('paste', aoColar)
    return () => window.removeEventListener('paste', aoColar)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desabilitado])

  const enviar = () => {
    const limpo = texto.trim()
    const arquivos = colados
    if (limpo === '' && arquivos.length === 0) return
    // Os colados vão primeiro (como no mensageiro): o texto fica de legenda logo abaixo.
    for (const c of arquivos) {
      onEnviarArquivo(c.arquivo, tipoDoArquivo(c.arquivo))
      if (c.previa) URL.revokeObjectURL(c.previa)
    }
    setColados([])
    if (limpo !== '') {
      // Só vai quem ainda está escrito: apagar o "@Fulano" desfaz a menção.
      const mencoes = [...marcados.current].filter(([, nome]) => limpo.includes(`@${nome}`)).map(([id]) => id)
      onEnviar(limpo, mencoes)
    }
    marcados.current.clear()
    setMencao(null)
    setTexto('')
  }

  if (desabilitado) {
    return (
      <div className="ch-readonly">
        <i className="fas fa-lock" aria-hidden />
        <span>{avisoDesabilitado}</span>
      </div>
    )
  }

  return (
    <div className="ch-composerwrap">
      {/* A citação em cima do campo: é o que mostra a quem você responde. */}
      {respondendo ? (
        <div className="ch-replybar">
          <span className="ch-quote ch-quote--static">
            <span className="ch-bubble__author">{respondendo.autor?.name ?? 'Alguém'}</span>
            <span className="ch-row__preview">{respondendo.corpo || 'Anexo'}</span>
          </span>
          <button type="button" className="ch-iconbtn" aria-label="Cancelar resposta" onClick={onCancelarResposta}>
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>
      ) : null}

      {colados.length > 0 ? (
        <div className="ch-colados" role="group" aria-label="Arquivos colados para enviar">
          {colados.map((c) => (
            <span key={c.id} className="ch-colados__item" title={c.arquivo.name}>
              {c.previa ? (
                <img className="ch-colados__imagem" src={c.previa} alt={c.arquivo.name} />
              ) : (
                <span className="ch-colados__arquivo">
                  <i className="fas fa-file-alt" aria-hidden />
                  <span className="ch-colados__nome">{c.arquivo.name}</span>
                  <span className="ch-colados__tamanho">{tamanhoLegivel(c.arquivo.size)}</span>
                </span>
              )}
              <button
                type="button"
                className="ch-colados__remover"
                aria-label={`Remover ${c.arquivo.name}`}
                onClick={() => removerColado(c.id)}
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </span>
          ))}
          <span className="ch-colados__dica">Enter envia · escreva uma legenda se quiser</span>
        </div>
      ) : null}

      <div className="ch-composer">
        <button
          type="button"
          className="ch-composer__tool"
          aria-label="Anexar"
          aria-haspopup="menu"
          title="Anexar"
          onClick={(e) => setMenuAnexo(e.currentTarget)}
        >
          <i className="fas fa-plus" aria-hidden />
        </button>

        <button
          type="button"
          className="ch-composer__tool"
          aria-label="Emojis e GIFs"
          aria-haspopup="dialog"
          title="Emojis e GIFs"
          onClick={(e) => setSeletorEmoji(e.currentTarget)}
        >
          <i className="far fa-smile" aria-hidden />
        </button>

        <textarea
          ref={campo}
          className="ch-composer__input"
          rows={1}
          value={texto}
          placeholder={colados.length > 0 ? 'Adicione uma legenda (opcional)' : 'Digite uma mensagem'}
          onChange={(e) => {
            setTexto(e.target.value)
            lerMencao(e.target.value, e.target.selectionStart ?? e.target.value.length)
            if (e.target.value !== '') onDigitar?.()
          }}
          onKeyDown={(e) => {
            // Com a lista de @ aberta, setas e Enter escolhem a pessoa.
            if (sugestoes.length > 0) {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault()
                const passo = e.key === 'ArrowDown' ? 1 : -1
                setDestaque((d) => (d + passo + sugestoes.length) % sugestoes.length)
                return
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault()
                escolher(sugestoes[Math.min(destaque, sugestoes.length - 1)]!)
                return
              }
            }
            // Enter envia; Shift+Enter deixa a textarea quebrar a linha
            // (SPEC-20261104-1538, como no mensageiro de referência).
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              enviar()
            }
          }}
        />

        <button
          type="button"
          className="ch-composer__send"
          aria-label="Enviar mensagem"
          title="Enviar mensagem"
          disabled={texto.trim() === '' && colados.length === 0}
          onClick={enviar}
        >
          <i className="fas fa-paper-plane" aria-hidden />
        </button>
      </div>

      <input
        ref={inputArquivo}
        type="file"
        hidden
        onChange={(e) => {
          const arquivo = e.target.files?.[0]
          if (arquivo) onEnviarArquivo(arquivo, tipoEscolhido.current)
          // Zerar o valor permite escolher o MESMO arquivo de novo depois.
          e.target.value = ''
        }}
      />

      {menuAnexo ? (
        <AnchoredOverlay
          anchor={menuAnexo}
          onClose={() => setMenuAnexo(null)}
          prefer="acima"
          // O `+` fica ~13px abaixo da borda da barra: com o gap padrao o menu
          // encostava no compositor. 22 deixa a barra inteira livre.
          gap={22}
          role="menu"
          aria-label="Anexar"
          className="ch-menu"
        >
          {MENU_DE_ANEXOS.map((item) => (
            <button
              key={item.tipo}
              type="button"
              role="menuitem"
              className="ch-more__item"
              onClick={() => {
                setMenuAnexo(null)
                if (item.accept === '') {
                  setFormulario(item.tipo as 'contato' | 'enquete' | 'evento')
                  return
                }
                tipoEscolhido.current = item.tipo
                if (inputArquivo.current) {
                  inputArquivo.current.accept = item.accept
                  inputArquivo.current.click()
                }
              }}
            >
              <i className={item.icone} aria-hidden />
              {item.label}
            </button>
          ))}
        </AnchoredOverlay>
      ) : null}

      {sugestoes.length > 0 && campo.current ? (
        <AnchoredOverlay
          anchor={campo.current}
          onClose={() => setMencao(null)}
          prefer="acima"
          gap={16}
          role="menu"
          aria-label="Mencionar"
          className="ch-menu ch-mentionlist"
        >
          <span className="ch-drawer__sub ch-menu__label">Mencionar</span>
          {sugestoes.map((pessoa, i) => {
            const foto = avatarDoAutor(pessoa)
            return (
              <button
                key={pessoa.memberId}
                type="button"
                role="menuitem"
                className={`ch-more__item${i === destaque ? ' ch-more__item--on' : ''}`}
                // mousedown: o clique no scrim fecharia a lista antes do click.
                onMouseDown={(e) => {
                  e.preventDefault()
                  escolher(pessoa)
                }}
              >
                <span className="ch-avatar ch-mentionlist__foto">
                  {foto ? <img className="ch-avatar__img" src={foto} alt="" /> : <i className="fas fa-user" aria-hidden />}
                </span>
                {pessoa.name}
              </button>
            )
          })}
        </AnchoredOverlay>
      ) : null}

      {seletorEmoji ? (
        <EmojiPicker
          anchor={seletorEmoji}
          onClose={() => setSeletorEmoji(null)}
          onEmoji={(emoji) => {
            // Entra onde o cursor está, não sempre no fim da frase.
            const el = campo.current
            const inicio = el?.selectionStart ?? texto.length
            const fim = el?.selectionEnd ?? texto.length
            setTexto((atual) => atual.slice(0, inicio) + emoji + atual.slice(fim))
            requestAnimationFrame(() => {
              el?.focus()
              el?.setSelectionRange(inicio + emoji.length, inicio + emoji.length)
            })
          }}
          onGif={(gif) => {
            setSeletorEmoji(null)
            onEnviarEstruturado({
              kind: 'gif',
              payload: { url: gif.url, titulo: gif.titulo, largura: gif.largura, altura: gif.altura },
            })
          }}
        />
      ) : null}

      {formulario ? (
        <FormularioEstruturado
          tipo={formulario}
          membros={membros}
          onCancelar={() => setFormulario(null)}
          onConfirmar={(dados) => {
            onEnviarEstruturado(dados)
            setFormulario(null)
          }}
        />
      ) : null}
    </div>
  )
}

/** Contato, enquete e evento — formulários curtos, no rodapé da conversa. */
function FormularioEstruturado({
  tipo,
  membros,
  onCancelar,
  onConfirmar,
}: {
  tipo: 'contato' | 'enquete' | 'evento'
  membros: readonly MembroDoCanal[]
  onCancelar: () => void
  onConfirmar: (dados: Estruturado) => void
}) {
  const [pergunta, setPergunta] = useState('')
  const [opcoes, setOpcoes] = useState(['', ''])
  const [multipla, setMultipla] = useState(false)
  const [titulo, setTitulo] = useState('')
  const [quando, setQuando] = useState('')

  return (
    <div className="ch-attachform">
      <div className="ch-drawer__head">
        <span className="ch-drawer__id">
          <span className="ch-drawer__title">
            {tipo === 'contato' ? 'Enviar contato' : tipo === 'enquete' ? 'Nova enquete' : 'Novo evento'}
          </span>
        </span>
        <button type="button" className="ch-drawer__back" aria-label="Fechar" onClick={onCancelar}>
          <i className="fas fa-times" aria-hidden />
        </button>
      </div>

      {tipo === 'contato' ? (
        <div className="ch-contacts">
          {membros.map((m) => (
            <button
              key={m.memberId}
              type="button"
              className="ch-contact"
              onClick={() =>
                onConfirmar({
                  kind: 'contato',
                  payload: { memberId: m.memberId, name: m.name, email: m.email },
                })
              }
            >
              <span className="ch-contact__avatar">
                <i className="fas fa-user" aria-hidden />
              </span>
              <span className="ch-contact__main">
                <span className="ch-contact__name">{m.name}</span>
                <span className="ch-contact__sub">{m.email}</span>
              </span>
            </button>
          ))}
        </div>
      ) : tipo === 'enquete' ? (
        <>
          <FieldBlock label="Pergunta" required>
            {({ id }) => <Input id={id} value={pergunta} onChange={(e) => setPergunta(e.target.value)} />}
          </FieldBlock>
          {opcoes.map((opcao, indice) => (
            <FieldBlock key={indice} label={`Opção ${indice + 1}`}>
              {({ id }) => (
                <div className="ch-enqueteform__opcao">
                  <Input
                    id={id}
                    value={opcao}
                    maxLength={200}
                    onChange={(e) =>
                      setOpcoes((atual) => atual.map((v, i) => (i === indice ? e.target.value : v)))
                    }
                  />
                  {opcoes.length > ENQUETE_MIN_OPCOES ? (
                    <button
                      type="button"
                      className="ch-iconbtn"
                      aria-label={`Remover opção ${indice + 1}`}
                      onClick={() => setOpcoes((atual) => atual.filter((_, i) => i !== indice))}
                    >
                      <i className="fas fa-times" aria-hidden />
                    </button>
                  ) : null}
                </div>
              )}
            </FieldBlock>
          ))}
          <Button
            variant="ghost"
            icon="fas fa-plus"
            disabled={opcoes.length >= ENQUETE_MAX_OPCOES}
            onClick={() => setOpcoes((a) => [...a, ''])}
          >
            Acrescentar opção
          </Button>
          <Checkbox
            className="ch-enqueteform__multipla"
            label="Permitir várias respostas"
            checked={multipla}
            onChange={(e) => setMultipla(e.target.checked)}
          />
          <div className="ch-drawer__foot">
            <Button variant="ghost" onClick={onCancelar}>
              Cancelar
            </Button>
            <Button
              disabled={pergunta.trim() === '' || opcoes.filter((o) => o.trim() !== '').length < 2}
              onClick={() =>
                onConfirmar({
                  kind: 'enquete',
                  payload: {
                    pergunta: pergunta.trim(),
                    opcoes: opcoes.map((o) => o.trim()).filter(Boolean),
                    multipla,
                  },
                })
              }
            >
              Enviar enquete
            </Button>
          </div>
        </>
      ) : (
        <>
          <FieldBlock label="Título do evento" required>
            {({ id }) => <Input id={id} value={titulo} onChange={(e) => setTitulo(e.target.value)} />}
          </FieldBlock>
          <FieldBlock label="Quando" hint="Ex.: 15/09 às 14h">
            {({ id }) => <Input id={id} value={quando} onChange={(e) => setQuando(e.target.value)} />}
          </FieldBlock>
          <div className="ch-drawer__foot">
            <Button variant="ghost" onClick={onCancelar}>
              Cancelar
            </Button>
            <Button
              disabled={titulo.trim() === ''}
              onClick={() =>
                onConfirmar({ kind: 'evento', payload: { titulo: titulo.trim(), quando: quando.trim() } })
              }
            >
              Enviar evento
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
